import React from "react";
import {
  HowToBuyAccordionContainer,
  BuyingStepsContainer,
  BuyingStepsHeadingContainer,
  StepDescription,
  StepIconContainer,
  StepTitleContainer,
  QRContainer
} from "./style";
import NextLink from "components/parts/NextLink";
import type { HowToBuyStepType } from "types/howToBuy-type";
import Accordion from "components/parts/Accordion";
import { Button, Flex, Text } from "@hubble/web";
import { IconMinus, IconPlus } from "@hubble/icons";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { Colors } from "themes/color-palette";
import {
  FontWeight,
  HubbleBody,
  HubbleDisplay,
  HubbleHeading
} from "components/parts/Typography";
import Image from "next/image";
import { handleContentfulLoader } from "utils/images/contentfulLoader";

interface BuyingStepsComponentProps {
  coinName: string;
  isMobile?: boolean;
}

const BuyingSteps = (props: BuyingStepsComponentProps) => {
  const { coinName, isMobile } = props;
  const t = useTranslations(FILES.HOW_TO_BUY);

  const getBuyingStepsArray = (coin: string): HowToBuyStepType[] => {
    const stepsArray: HowToBuyStepType[] = [
      {
        stepName: t("step1"),
        stepLabel: <React.Fragment>{t("step1Label")}</React.Fragment>,
        stepDescription: (
          <React.Fragment>
            {t("step1DescriptionP1")}&nbsp;
            <NextLink
              style={{ display: "inline-block" }}
              newTab
              href="https://exchange.gemini.com/register"
              id="how-to-buy-steps-cta"
            >
              <Text as={"span"} style={{ whiteSpace: "nowrap" }} underline>
                {t("step1DescriptionP1Link")}&nbsp;
              </Text>
            </NextLink>
            <br />
            <br />
            {isMobile ? (
              <React.Fragment>
                {t("step1DescriptionP2Web")}
                <br />
                <Button.Primary
                  href="https://www.google.com/url?q=https://geminiexchange.app.link/1LQ86wZHvCb&sa=D&source=docs&ust=1692958191606726&usg=AOvVaw09e9UGKNzDcWqOf8Nj6_UQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  role="link"
                  style={{ textDecoration: "none" }}
                  mt={2}
                >
                  {t("downloadApp")}
                </Button.Primary>
              </React.Fragment>
            ) : (
              <Flex align="center" wrap="wrap" gap={"1rem"}>
                <QRContainer>
                  <Image
                    src={
                      "https://images.ctfassets.net/jg6lo9a2ukvr/6BLawWAjIy4XUYtgrAbO2f/23370d6261b879c021a330ae74d5142e/QR_download_app.svg"
                    }
                    alt={"Donload App - qr"}
                    height={64}
                    width={64}
                    loader={handleContentfulLoader}
                  />
                </QRContainer>{" "}
                <Text style={{ flex: 1, maxWidth: "500px" }}>
                  {t("step1DescriptionP2Mobile")}
                </Text>
              </Flex>
            )}
          </React.Fragment>
        )
      },
      {
        stepName: t("step2"),
        stepLabel: <React.Fragment>{t("step2Label")}</React.Fragment>,
        stepDescription: (
          <React.Fragment>
            {t("step2Description")}&nbsp;
            <NextLink
              style={{ display: "inline-block" }}
              newTab
              href="https://support.gemini.com/hc/articles/360020715531-How-do-I-verify-my-identity-in-the-app-"
            >
              <Text as={"span"} style={{ whiteSpace: "nowrap" }} underline>
                {t("step2DescriptionLinkLabel")}&nbsp;
              </Text>
            </NextLink>
          </React.Fragment>
        )
      },
      {
        stepName: t("step3"),
        stepLabel: <React.Fragment>{t("step3Label")}</React.Fragment>,
        stepDescription: (
          <React.Fragment>
            {t("step3Description")}&nbsp;
            <NextLink
              style={{ display: "inline-block" }}
              newTab
              href="https://www.gemini.com/blog/funding-crypto-purchases-on-your-gemini-account"
            >
              <Text as={"span"} style={{ whiteSpace: "nowrap" }} underline>
                {t("step3DescriptionLinkLabel")}&nbsp;
              </Text>
            </NextLink>
          </React.Fragment>
        )
      },
      {
        stepName: t("step4"),
        stepLabel: <React.Fragment>{t("step4Label", { coin })}</React.Fragment>,
        stepDescription: (
          <React.Fragment>{t("step4Description")}</React.Fragment>
        )
      },
      {
        stepName: t("step5"),
        stepLabel: <React.Fragment>{t("step5Label", { coin })}</React.Fragment>,
        stepDescription: (
          <React.Fragment>{t("step5Description")}</React.Fragment>
        )
      },
      {
        stepName: t("step6"),
        stepLabel: <React.Fragment>{t("step6Label")}</React.Fragment>,
        stepDescription: (
          <React.Fragment>{t("step6Description")}</React.Fragment>
        )
      }
    ];

    return stepsArray;
  };

  return (
    <BuyingStepsContainer>
      <BuyingStepsHeadingContainer>
        <HubbleDisplay size="lg" bold as={"h2"} color={Colors.gray[600]} mb={2}>
          {t("howToBuy")}
        </HubbleDisplay>
        <HubbleHeading as={"span"} size="sm" color={Colors.gray[600]}>
          {t("footerSectionLinkText", { coinName })}
        </HubbleHeading>
      </BuyingStepsHeadingContainer>
      <HowToBuyAccordionContainer>
        <Accordion>
          {getBuyingStepsArray(coinName).map(
            ({ stepDescription, stepLabel, stepName }) => (
              <Accordion.Collapse
                customTitle={(isOpen) => (
                  <Flex align="center" justify="space-between" width={"100%"}>
                    <StepTitleContainer>
                      <HubbleBody
                        as={"h3"}
                        fontWeight={parseInt(FontWeight.semiBold)}
                        size={isMobile ? "md" : "lg"}
                        color={Colors.gray[600]}
                        margin={0}
                      >
                        {stepName}
                      </HubbleBody>

                      <HubbleHeading
                        fontWeight={parseInt(FontWeight.semiBold)}
                        as={"h3"}
                        size="sm"
                        color={Colors.black_secondary}
                      >
                        {" "}
                        {stepLabel}
                      </HubbleHeading>
                    </StepTitleContainer>

                    <StepIconContainer justify="center" align="center">
                      {!isOpen ? <IconPlus /> : <IconMinus />}
                    </StepIconContainer>
                  </Flex>
                )}
                titleAs={"h3"}
              >
                <StepDescription
                  fontWeight={parseInt(FontWeight.semiBold)}
                  size="md"
                  color={Colors.gray[600]}
                  align={"left"}
                >
                  {stepDescription}
                </StepDescription>
              </Accordion.Collapse>
            )
          )}
        </Accordion>
      </HowToBuyAccordionContainer>
    </BuyingStepsContainer>
  );
};
export default BuyingSteps;
